import type {ToolCallMessagePartComponent} from '@assistant-ui/react';
import {Button, IconButton} from '@wandb/components';
import {
  MOON_200,
  MOON_300,
  MOON_450,
  MOON_650,
  MOON_700,
  MOON_800,
  TEAL_500,
  WB_RUN_COLORS,
  WHITE,
} from '@wandb/weave/common/css/color.styles';
import {
  Content,
  Overlay,
  Portal,
  Root,
  Title,
} from '@wandb/weave/components/Dialog/Dialog';
import type * as Plotly from 'plotly.js';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useColorMode} from '../../../util/useColorMode';

export const PLOTLY_PANEL_TOOL_NAME = 'wbui__inchat_plotly_panel';
/** Kept only so persisted transcripts using the original name still render. */
export const LEGACY_PLOTLY_CHART_TOOL_NAME = 'wbui__plotly_chart';

type PlotlyPanelArgs = {
  data?: Plotly.Data[];
  layout?: Partial<Plotly.Layout>;
};

type PlotlyModule = typeof import('plotly.js');

const DEFAULT_CHART_HEIGHT = 360;
const DEFAULT_MARGIN: Partial<Plotly.Margin> = {t: 48, r: 24, b: 48, l: 56};
const RENDER_ERROR = 'This visualization could not be rendered.';
const UNTITLED_FIGURE_LABEL = 'Visualization';
const ADVISORY_VALIDATION_CODES = new Set(['unused', 'dynamic', 'invisible']);
const PLOT_CONFIG: Partial<Plotly.Config> = {
  displaylogo: false,
  displayModeBar: false,
  responsive: true,
  scrollZoom: false,
};

const FULL_SCREEN_PLOT_CONFIG: Partial<Plotly.Config> = {
  ...PLOT_CONFIG,
  displayModeBar: true,
  modeBarButtonsToRemove: ['toImage', 'select2d', 'lasso2d'],
  scrollZoom: true,
};

export function buildPlotlyLayout(
  layout: Partial<Plotly.Layout> = {},
  isDarkMode = false
): Partial<Plotly.Layout> {
  const textColor = isDarkMode ? MOON_300 : MOON_650;
  const gridColor = isDarkMode ? MOON_700 : MOON_200;
  const axisColor = isDarkMode ? MOON_650 : MOON_300;

  const axisTheme: Partial<Plotly.LayoutAxis> = {
    automargin: true,
    gridcolor: gridColor,
    linecolor: axisColor,
    tickfont: {color: textColor},
    zerolinecolor: gridColor,
  };
  const theme: Partial<Plotly.Layout> = {
    autosize: true,
    height: DEFAULT_CHART_HEIGHT,
    colorway: WB_RUN_COLORS,
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
  };

  return {
    ...theme,
    ...layout,
    font: {color: textColor, ...layout.font},
    hoverlabel: {
      bgcolor: isDarkMode ? MOON_800 : WHITE,
      bordercolor: axisColor,
      font: {color: textColor},
      ...layout.hoverlabel,
    },
    margin: {...DEFAULT_MARGIN, ...layout.margin},
    modebar: {
      activecolor: TEAL_500,
      bgcolor: 'transparent',
      color: isDarkMode ? MOON_450 : MOON_650,
      ...layout.modebar,
    },
    xaxis: {...axisTheme, ...layout.xaxis},
    yaxis: {...axisTheme, ...layout.yaxis},
  };
}

function getFigureTitle(layout: Partial<Plotly.Layout>): string | undefined {
  const {title} = layout;
  if (typeof title === 'string') {
    return title;
  }
  return typeof title?.text === 'string' ? title.text : undefined;
}

type PlotlyFigureProps = {
  className: string;
  config: Partial<Plotly.Config>;
  data: Plotly.Data[];
  layout: Partial<Plotly.Layout>;
  onRenderError: (message: string) => void;
  plotly: PlotlyModule;
};

const PlotlyFigure = ({
  className,
  config,
  data,
  layout,
  onRenderError,
  plotly,
}: PlotlyFigureProps) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart == null) {
      return;
    }
    void plotly
      .react(chart, data, layout, config)
      .catch(() => onRenderError(RENDER_ERROR));
  }, [config, data, layout, onRenderError, plotly]);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart == null || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      void plotly.Plots.resize(chart);
    });
    observer.observe(chart);
    return () => observer.disconnect();
  }, [plotly]);

  useEffect(() => {
    const chart = chartRef.current;
    return () => {
      if (chart != null) {
        plotly.purge(chart);
      }
    };
  }, [plotly]);

  return <div ref={chartRef} className={className} />;
};

/**
 * Render a backend-owned Plotly UI action as a responsive transcript chart.
 * A call the backend rejected (`isError`) renders nothing: the model receives
 * the validation details and answers with a corrected figure.
 */
export const PlotlyPanelToolUI: ToolCallMessagePartComponent<
  PlotlyPanelArgs
> = ({args, isError}) => {
  const [plotly, setPlotly] = useState<PlotlyModule | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const {isDarkMode} = useColorMode();

  const data = useMemo(
    () => (Array.isArray(args?.data) ? args.data : []),
    [args?.data]
  );
  const figureLayout = useMemo(() => args?.layout ?? {}, [args?.layout]);
  const layout = useMemo(
    () => buildPlotlyLayout(figureLayout, isDarkMode),
    [figureLayout, isDarkMode]
  );
  const fullScreenLayout = useMemo(() => {
    const {height, ...rest} = layout;
    return rest;
  }, [layout]);
  const validationError = useMemo(
    () => (plotly == null ? null : validateFigure(plotly, data, figureLayout)),
    [data, figureLayout, plotly]
  );

  const onRenderError = useCallback(
    (message: string) => setRenderError(message),
    []
  );
  const onFullScreenChange = useCallback((open: boolean) => {
    setIsFullScreen(open);
    setRenderError(null);
  }, []);

  useEffect(() => {
    let active = true;
    void import('plotly.js').then(module => {
      if (active) {
        setPlotly(module);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const isRenderable =
    plotly != null && data.length > 0 && validationError == null;
  const error = validationError ?? renderError;
  const title = getFigureTitle(figureLayout);

  if (isError) {
    return null;
  }

  return (
    <div className="group relative my-8 overflow-hidden rounded-lg bg-wb-surface-raised">
      {isRenderable && (
        <div className="absolute right-8 top-8 z-10 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <IconButton
            aria-label="View full screen"
            data-dd-action-name="View full screen"
            icon="full-screen-mode-expand"
            onPress={() => onFullScreenChange(true)}
            size="sm"
            variant="ghost"
          />
        </div>
      )}
      {isRenderable && plotly != null && (
        <PlotlyFigure
          className="min-h-[360px] w-full"
          config={PLOT_CONFIG}
          data={data}
          layout={layout}
          onRenderError={onRenderError}
          plotly={plotly}
        />
      )}
      {(plotly == null || data.length === 0) && (
        <div className="flex h-96 items-center justify-center text-sm text-wb-text-tertiary">
          Preparing visualization…
        </div>
      )}
      {error && (
        <div className="border-t border-wb-border-divider px-12 py-8 text-wb-error-text">
          {error}
        </div>
      )}
      {isFullScreen && plotly != null && (
        <Root open onOpenChange={onFullScreenChange}>
          <Portal>
            <Overlay />
            <Content
              className="flex h-[80vh] w-[90vw] max-w-[1200px] flex-col overflow-hidden rounded-lg p-0"
              data-test="plotly-panel-fullscreen-modal">
              <Title className="sr-only">
                {title ?? UNTITLED_FIGURE_LABEL}
              </Title>
              <div className="min-h-0 flex-1 overflow-hidden bg-wb-surface p-16">
                <PlotlyFigure
                  className="h-full w-full"
                  config={FULL_SCREEN_PLOT_CONFIG}
                  data={data}
                  layout={fullScreenLayout}
                  onRenderError={onRenderError}
                  plotly={plotly}
                />
              </div>
              <div className="flex shrink-0 items-center justify-end gap-12 border-t border-solid border-wb-border bg-wb-surface-sunken px-16 py-12">
                <Button
                  onPress={() => onFullScreenChange(false)}
                  variant="secondary">
                  Close
                </Button>
              </div>
            </Content>
          </Portal>
        </Root>
      )}
    </div>
  );
};

function validateFigure(
  plotly: PlotlyModule,
  data: Plotly.Data[],
  layout: Partial<Plotly.Layout>
): string | null {
  const error = plotly
    .validate(data, layout)
    ?.find(({code}) => !ADVISORY_VALIDATION_CODES.has(code));
  return error?.msg ?? null;
}
